import type { EducationLevelFilterInput, EducationLevelMode } from '@/features/recruiter/api/candidates.api'

interface EducationLevelFilterFieldsProps {
  label: string
  value: EducationLevelFilterInput
  onChange: (value: EducationLevelFilterInput) => void
}

const MODE_OPTIONS: { value: EducationLevelMode; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'specific', label: 'Specific' },
  { value: 'none', label: 'No qualification' },
]

/**
 * One of the three independent education-level sections (UG/PG/Doctorate)
 * from the spec — any/specific/none, with degree/specialization/
 * university/year/percentage sub-fields shown only when "specific" is
 * selected. See EducationLevelFilter (backend) for how each mode maps to
 * a nested query.
 */
export function EducationLevelFilterFields({ label, value, onChange }: EducationLevelFilterFieldsProps) {
  const idPrefix = label.replace(/\s+/g, '-').toLowerCase()

  return (
    <div className="mb-3">
      <span className="form-label fw-medium d-block">{label}</span>
      <div className="d-flex gap-3 mb-2">
        {MODE_OPTIONS.map((opt) => (
          <div className="form-check" key={opt.value}>
            <input
              type="radio"
              className="form-check-input"
              id={`${idPrefix}-${opt.value}`}
              name={`${idPrefix}-mode`}
              checked={value.mode === opt.value}
              onChange={() => onChange({ mode: opt.value })}
            />
            <label className="form-check-label fs-13" htmlFor={`${idPrefix}-${opt.value}`}>
              {opt.label}
            </label>
          </div>
        ))}
      </div>

      {value.mode === 'specific' && (
        <div className="row g-2">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Degree"
              value={value.degree ?? ''}
              onChange={(e) => onChange({ ...value, degree: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Specialization"
              value={value.specialization ?? ''}
              onChange={(e) => onChange({ ...value, specialization: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="University / Institute"
              value={value.university ?? ''}
              onChange={(e) => onChange({ ...value, university: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-3">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Year from"
              value={value.year_from ?? ''}
              onChange={(e) => onChange({ ...value, year_from: e.target.value ? Number(e.target.value) : undefined })}
            />
          </div>
          <div className="col-md-3">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Year to"
              value={value.year_to ?? ''}
              onChange={(e) => onChange({ ...value, year_to: e.target.value ? Number(e.target.value) : undefined })}
            />
          </div>
          <div className="col-md-6">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Minimum percentage / CGPA"
              value={value.percentage_min ?? ''}
              onChange={(e) =>
                onChange({ ...value, percentage_min: e.target.value ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}
